import { useCallback, useEffect, useMemo, useState } from "react";
import { deleteRow, insertRow, listRows, updateRow } from "@/lib/adminApi";
import {
  FAQ_CATEGORIES,
  FAQ_SCOPES,
  FAQ_STATUS,
  metaFor,
  optionsFrom,
  type Faq,
} from "@/lib/adminContent";
import {
  DataTable,
  Drawer,
  EmptyState,
  ErrorBanner,
  Field,
  FilterBar,
  LoadingState,
  Modal,
  PageHeader,
  SearchInput,
  Select,
  StatCards,
  StatusPill,
  TextField,
  btnGhost,
  btnPrimary,
  fieldInput,
  textInput,
  type Column,
} from "@/pages/admin/components/AdminUI";
import { useAdminToast } from "@/hooks/useAdminToast";

const EMPTY_FAQ = {
  question: "",
  answer: "",
  category: "General",
  scope: "General",
  sort_order: 0,
  status: "published",
};

export default function AdminFaqs() {
  const [rows, setRows] = useState<Faq[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [scopeFilter, setScopeFilter] = useState("all");
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [draft, setDraft] = useState({ ...EMPTY_FAQ });
  const [saving, setSaving] = useState(false);
  const { notify, node } = useAdminToast();

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      setRows(await listRows<Faq>("faqs", "sort_order"));
    } catch (err) {
      setError(err instanceof Error && err.message ? err.message : "We couldn't load FAQs.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const update = async (id: number, patch: Partial<Faq>, message?: string) => {
    setRows((list) => list.map((row) => (row.id === id ? { ...row, ...patch } : row)));
    try {
      await updateRow("faqs", id, patch);
      if (message) notify(message);
    } catch {
      setError("We couldn't save that change. Please try again.");
      load();
    }
  };

  const createFaq = async () => {
    if (!draft.question.trim() || !draft.answer.trim()) {
      setError("A question and an answer are both required.");
      return;
    }
    setSaving(true);
    try {
      const created = await insertRow<Faq>("faqs", { ...draft, question: draft.question.trim() });
      setRows((list) => [...list, created]);
      setCreateOpen(false);
      setDraft({ ...EMPTY_FAQ });
      notify("FAQ added.");
    } catch {
      setError("We couldn't create that FAQ. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    try {
      await deleteRow("faqs", id);
      setRows((list) => list.filter((row) => row.id !== id));
      setSelectedId(null);
      notify("FAQ deleted.");
    } catch {
      setError("We couldn't delete that FAQ. Please try again.");
    }
  };

  const filtered = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      if (scopeFilter !== "all" && row.scope !== scopeFilter) return false;
      if (!query) return true;
      return [row.question, row.answer, row.category || "", row.scope || ""].join(" ").toLowerCase().includes(query);
    });
  }, [rows, search, statusFilter, scopeFilter]);

  const selected = rows.find((row) => row.id === selectedId) || null;

  const columns: Column<Faq>[] = [
    {
      key: "question",
      header: "Question",
      primary: true,
      render: (row) => (
        <div className="min-w-0 max-w-md">
          <p className="text-sm font-semibold text-foreground-950">{row.question}</p>
          <p className="text-xs text-foreground-500 mt-0.5 line-clamp-1">{row.answer}</p>
        </div>
      ),
    },
    {
      key: "scope",
      header: "Scope",
      render: (row) => (
        <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full whitespace-nowrap">
          {row.scope || "General"}
        </span>
      ),
    },
    { key: "category", header: "Category", render: (row) => <span className="text-sm text-foreground-700">{row.category || "—"}</span> },
    { key: "status", header: "Status", render: (row) => <StatusPill {...metaFor(FAQ_STATUS, row.status)} /> },
  ];

  return (
    <div>
      <PageHeader
        eyebrow="Content"
        title="FAQs"
        subtitle="Every question and answer shown across the website — grouped by scope so the right ones appear on the right pages."
        actions={
          <button type="button" onClick={() => setCreateOpen(true)} className={btnPrimary}>
            <i className="ri-add-line"></i> New FAQ
          </button>
        }
      />

      <StatCards
        stats={[
          { label: "Total FAQs", value: rows.length, icon: "ri-question-line", tone: "primary" },
          { label: "Published", value: rows.filter((row) => row.status === "published").length, icon: "ri-checkbox-circle-line", tone: "primary" },
          { label: "Scopes covered", value: new Set(rows.map((row) => row.scope)).size, icon: "ri-stack-line", tone: "secondary" },
          { label: "Drafts", value: rows.filter((row) => row.status !== "published").length, icon: "ri-draft-line", tone: "accent" },
        ]}
      />

      {error ? (
        <div className="mt-5">
          <ErrorBanner message={error} onRetry={load} onDismiss={() => setError("")} />
        </div>
      ) : null}

      <div className="mt-5 space-y-5">
        <FilterBar>
          <SearchInput value={search} onChange={setSearch} placeholder="Search questions and answers…" />
          <Select label="Status" value={statusFilter} onChange={setStatusFilter} options={[{ value: "all", label: "All statuses" }, ...optionsFrom(FAQ_STATUS)]} />
          <Select label="Scope" value={scopeFilter} onChange={setScopeFilter} options={[{ value: "all", label: "All scopes" }, ...FAQ_SCOPES.map((scope) => ({ value: scope, label: scope }))]} />
          <span className="text-sm text-foreground-500 whitespace-nowrap lg:ml-auto">{filtered.length} FAQs</span>
        </FilterBar>

        {loading ? (
          <LoadingState label="Loading FAQs…" />
        ) : filtered.length === 0 ? (
          <EmptyState icon="ri-question-line" title={rows.length === 0 ? "No FAQs yet" : "No FAQs match"} message="Add a question to answer it on the site." />
        ) : (
          <DataTable columns={columns} rows={filtered} onRowClick={(row) => setSelectedId(row.id)} />
        )}
      </div>

      <Drawer
        open={Boolean(selected)}
        onClose={() => setSelectedId(null)}
        title="FAQ"
        subtitle="Question detail"
        footer={
          selected ? (
            <button type="button" onClick={() => remove(selected.id)} className={btnGhost}>
              <i className="ri-delete-bin-line"></i> Delete FAQ
            </button>
          ) : null
        }
      >
        {selected ? (
          <>
            <div className="flex flex-wrap items-center gap-2">
              <StatusPill {...metaFor(FAQ_STATUS, selected.status)} />
              <span className="text-xs font-semibold text-secondary-700 bg-secondary-100 border border-secondary-200 px-2.5 py-1 rounded-full">{selected.scope || "General"}</span>
            </div>

            <Field label="Question">
              <input value={selected.question} onChange={(e) => update(selected.id, { question: e.target.value })} className={textInput} />
            </Field>
            <Field label="Answer">
              <textarea
                value={selected.answer}
                onChange={(e) => update(selected.id, { answer: e.target.value })}
                rows={5}
                maxLength={500}
                className={`${textInput} resize-none`}
              ></textarea>
            </Field>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Field label="Scope">
                <select value={selected.scope || "General"} onChange={(e) => update(selected.id, { scope: e.target.value }, "Scope updated.")} className={fieldInput}>
                  {FAQ_SCOPES.map((scope) => (
                    <option key={scope} value={scope}>{scope}</option>
                  ))}
                </select>
              </Field>
              <Field label="Category">
                <select value={selected.category || "General"} onChange={(e) => update(selected.id, { category: e.target.value })} className={fieldInput}>
                  {FAQ_CATEGORIES.map((category) => (
                    <option key={category} value={category}>{category}</option>
                  ))}
                </select>
              </Field>
            </div>

            <Field label="Publication status">
              <select value={selected.status} onChange={(e) => update(selected.id, { status: e.target.value }, "FAQ status updated.")} className={fieldInput}>
                {optionsFrom(FAQ_STATUS).map((option) => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
              </select>
            </Field>
          </>
        ) : null}
      </Drawer>

      <Modal
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        title="Add an FAQ"
        subtitle="New question"
        footer={
          <>
            <button type="button" onClick={() => setCreateOpen(false)} className={btnGhost}>Cancel</button>
            <button type="button" onClick={createFaq} disabled={saving} className={btnPrimary}>
              {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-check-line"></i>}
              Add FAQ
            </button>
          </>
        }
      >
        <TextField label="Question">
          <input value={draft.question} onChange={(e) => setDraft({ ...draft, question: e.target.value })} placeholder="e.g. What should I bring?" className={textInput} />
        </TextField>
        <TextField label="Answer">
          <textarea value={draft.answer} onChange={(e) => setDraft({ ...draft, answer: e.target.value })} rows={4} maxLength={500} className={`${textInput} resize-none`}></textarea>
        </TextField>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <TextField label="Scope">
            <select value={draft.scope} onChange={(e) => setDraft({ ...draft, scope: e.target.value })} className={fieldInput}>
              {FAQ_SCOPES.map((scope) => (<option key={scope} value={scope}>{scope}</option>))}
            </select>
          </TextField>
          <TextField label="Category">
            <select value={draft.category} onChange={(e) => setDraft({ ...draft, category: e.target.value })} className={fieldInput}>
              {FAQ_CATEGORIES.map((category) => (<option key={category} value={category}>{category}</option>))}
            </select>
          </TextField>
        </div>
      </Modal>

      <p className="text-xs text-foreground-500 mt-5">
        <i className="ri-information-line"></i> Changes save automatically.
      </p>
      {node}
    </div>
  );
}